import { ContentFooter } from "@app/components/core/ContentFooter/ContentFooter"
import { ContentHeader } from "@app/components/core/ContentHeader/ContentHeader"
import RetryError from "@app/components/core/RetryError"
import { Checkbox } from "@app/components/move2Library/Checkbox/Checkbox"
import { useCustomNavigation } from "@app/components/navigation/NavigationHandler"
import { Addresses } from "@app/components/order/Addresses/Addresses"
import Entrances from "@app/components/order/Entrances/Entrances"
import { LinkList } from "@app/components/order/LinkList/LinkList"
import OrderContentBody from "@app/components/order/OrderContentBody/OrderContentBody"
import { ParkingTickets } from "@app/components/order/ParkingTickets/ParkingTickets"
import { PriceSummary } from "@app/components/order/PriceSummary/PriceSummary"
import Products from "@app/components/order/Products/Products"
import Thermencard from "@app/components/order/Thermencard/Thermencard"
import UsedVouchers from "@app/components/order/UsedVouchers/UsedVouchers"
import OrderSubPage from "@app/components/pageTypes/OrderSubPage/OrderSubPage"
import { OrderAnimationTypes } from "@app/components/routing/AnimatedOrderRoute/AnimatedOrderRoute"
import TerminalMessageModal from "@app/modals/funnel/TerminalMessage/TerminalMessageModal"
import { useRaiseError } from "@app/store/actions/errorActions"
import { setFormattedOrder } from "@app/store/actions/orderActions"
import { useGetLinks, useGetLoungerTypes, useGetPaymentGroups, useGetProducts, useGetStrings, useGetUpsellArticles } from "@app/store/service/cmsService"
import { useFormatOrder, useGetAmazonSession, useGetTransporterCountries, useSaveOrder } from "@app/store/service/connectorService"
import { appStore } from "@app/store/store"
import { IframeMessages, LinkTypes, ORDER_LINE_TYPES, OrderErrors, PAYMENT_TYPES } from "@app/utils/constants"
import { getTransporterIdsForCountrySelection } from "@app/utils/getTransporterIds"
import { RenderHTML } from "@app/utils/htmlUtils"
import tracking from "@app/utils/tracking"
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons/faCircleNotch"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { KioskBridge } from "@tecreation/bird5-kiosk-bridge"
import { useModal, useViewport } from "@tecreation/funnel-components"
import { useSelector } from "@tecreation/js-state-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useForm } from "react-hook-form"

const getLinkUrl = (linkData = {}) => {
	if (appStore.get("core.kioskMode")) return linkData.kioskBaseUrl
	return linkData.baseUrl
}

export default function OrderOverview2() {
	const formattedOrder = useSelector(state => state.order.formattedOrder)
	const location = useSelector(state => state.core.location)
	const shoppingCart = useSelector(state => state.order.shoppingCart)
	const massages = useSelector(state => state.order.massage)
	const { data: transporterCountries } = useGetTransporterCountries({ transporterIds: getTransporterIdsForCountrySelection(formattedOrder) })
	let deliveryAddress = useSelector("core.deliveryAddress")
	let invoiceAddress = useSelector("core.invoiceAddress")
	const { sm, md } = useViewport()
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm({ mode: "onSubmit" })
	const { push } = useCustomNavigation()
	const raiseError = useRaiseError()
	const { showModal, hideModal } = useModal()
	const processing = useRef(false)
	const [addInfo, setAddInfo] = useState("")
	const amazonCheckoutSessionId = useSelector("core.amazonCheckoutSessionId")
	const { data: amazonPayData } = useGetAmazonSession(
		{ paymentId: formattedOrder.paymentId, checkoutSessionId: amazonCheckoutSessionId },
		{ skip: !amazonCheckoutSessionId },
	)
	const { data: paymentGroups, isLoading: paymentGroupsLoading } = useGetPaymentGroups()
	const {
		data: {
			buttonBuy,
			orderOverviewTitle,
			orderOverviewMandatoryTitle,
			orderOverviewEmailInformationTitle,
			orderOverviewAgbCheckbox,
			orderOverviewRevocationCheckbox,
			// orderOverviewNewsletterTitle,
			// orderOverviewNewsletterCheckbox,
			orderOverviewAdditionalInfo,
			errorInputRequiredField,
		},
		isLoading: stringLoading,
	} = useGetStrings(undefined, { defaultData: {} })
	const [saveOrder, { isFetching }] = useSaveOrder()
	const [__formatOrder, { isFetching: formatFetching, error: formatError }] = useFormatOrder()
	const { data: loungerTypes } = useGetLoungerTypes({ locationId: appStore.getState().core.locationId })
	const { data: parkingTickets } = useGetUpsellArticles()
	const { data: baseProducts } = useGetProducts()
	const { data: linkData } = useGetLinks()

	let shouldDisableBecauseOfTransporterCountry = useMemo(() => {
		if (transporterCountries) {
			const hasTransporterType4 = formattedOrder?.orderLines?.some(line => line.transporterTypeId === 4)

			const isCountryMatching = transporterCountries?.some(
				country => invoiceAddress?.countryId == country.countryId || deliveryAddress?.countryId == country.countryId,
			)
			return hasTransporterType4 ? !isCountryMatching : false
		}

		return false
	}, [formattedOrder, invoiceAddress, deliveryAddress, transporterCountries])

	useEffect(() => {
		if (shouldDisableBecauseOfTransporterCountry) {
			raiseError(new Error(OrderErrors.deliveryCountryNotValid))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldDisableBecauseOfTransporterCountry])

	const formatOrder = useCallback(
		(loungerTypes, parkingTickets, baseProducts) => {
			if (!amazonCheckoutSessionId || (amazonCheckoutSessionId && amazonPayData !== undefined)) {
				return __formatOrder({})
					.unwrap()
					.then(rsp => {
						setFormattedOrder(rsp, loungerTypes, parkingTickets, baseProducts)
						return rsp
					})
					.catch(e => {
						raiseError(e, undefined, false, true)
					})
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[amazonCheckoutSessionId, amazonPayData],
	)

	const next = useCallback(() => {
		if (processing.current === true) return
		processing.current = true
		handleSubmit(
			async () => {
				if (appStore.get("core.kioskMode") === true) {
					let paymentId = paymentGroups.find(e => e.type === PAYMENT_TYPES.EC_Terminal)?.paymentId
					try {
						showModal(TerminalMessageModal)

						let paymentLine = undefined
						if (formattedOrder.payAmount > 0) {
							paymentLine = await KioskBridge.execute(
								"payment.keyboard.kiosk",
								{
									paymentId: paymentId,
									payAmount: formattedOrder.payAmount,
									addToReceipt: false,
								},
								message => {
									appStore.set(state => ({
										order: {
											...state.order,
											ecMessage: message,
										},
									}))
								},
							)
						}

						const fo = await __formatOrder({ paymentLine }).unwrap()
						const swissbit = await KioskBridge.blackbox.getParams()
						if (swissbit) {
							await KioskBridge.blackbox.startTransaction(JSON.parse(JSON.stringify(fo)))
						}
						const startTransaction = await KioskBridge.blackbox.getCurrentTransaction()
						if (startTransaction) {
							const endTransaction = await KioskBridge.blackbox.endTransaction(startTransaction.order, startTransaction)
							fo.blackbox = {
								transactions: [startTransaction, endTransaction],
							}
						}
						appStore.set("order.formattedOrder", fo)
						const result = await saveOrder()
						KioskBridge.execute("printer.kiosk.print.receipt", { divisionId: result.divisionId })
						push("/kiosk-succeed")
						hideModal()

						if (appStore.get("core.kioskMode") || appStore.get("core.employeeMode")) {
							KioskBridge.execute("receipt.kiosk.stop").catch(() => {
								// raiseError(e instanceof Error ? e : new Error(e));
							})
						}
					} catch (e) {
						processing.current = false
						raiseError(e instanceof Error ? e : new Error(e), undefined, false, true)
						hideModal()
					}
				} else {
					try {
						let result = await saveOrder().unwrap()

						tracking.startPurchase(formattedOrder)

						let url = ""
						if (result.action === "open.url") {
							url = result.url
						} else if (result.action === "order.close") {
							let webToken = result.webToken
							let orderNumber = result.orderNumber
							push("/order-succeed" + "?success=true" + (webToken ? "&webToken=" + webToken : "") + (orderNumber ? "&orderNumber=" + orderNumber : ""))
							return
						}

						if (window.self !== window.top) {
							window.parent.postMessage(IframeMessages.openUrl + ";" + url, "*")
						} else {
							if (!url || url.length === 0) {
								raiseError(new Error("url is empty"), undefined, false, true)
							}
							window.location.href = url
						}
					} catch (e) {
						processing.current = false
						if (e && e.message === "not enough amount") {
							return raiseError(e, undefined, true)
						}

						if (e instanceof Error && e.message === "err.massageLifetimeEnded") {
							let invalidLines = e.invalidLines

							invalidLines.forEach(line => {
								if (line.typeId === ORDER_LINE_TYPES.Activity) {
									let massage = appStore.get("order.massage")
									if (massage?.exportCode === line.exportCode) {
										appStore.set("order.massage", undefined)
									}

									let shoppingCart = appStore.get("order.shoppingCart")

									if (shoppingCart && shoppingCart.length) {
										shoppingCart.filter(e => e.exportCode !== line.exportCode)
									}
									appStore.set("order.shoppingCart")

									formatOrder(loungerTypes, parkingTickets, baseProducts).then(e => {
										if (e.orderLines.length === 0) {
											push("/")
										}
									})
								}
							})

							return raiseError(e, undefined, false)
						}

						raiseError(e, undefined, false, true)
					}
				}
			},
			() => {
				processing.current = false
			},
		)()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paymentGroups, formattedOrder, loungerTypes, parkingTickets, baseProducts])

	if (!String.prototype.replaceAll) {
		String.prototype.replaceAll = function (str, newStr) {
			// If a regex pattern
			if (Object.prototype.toString.call(str).toLowerCase() === "[object regexp]") {
				return this.replace(str, newStr)
			}
			// If a string
			return this.replace(new RegExp(str, "g"), newStr)
		}
	}

	useEffect(() => {
		let linkTermsConditions = getLinkUrl(linkData) + appStore.get("core.languageCode") + "/" + linkData?.links.find(e => e.type === LinkTypes.privacy).slug
		let next = (orderOverviewAdditionalInfo || "")
			.replaceAll("${email}", location ? location.email : "")
			.replaceAll("${linkTermsConditions}", linkTermsConditions ? linkTermsConditions : "")
		setAddInfo(next)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orderOverviewAdditionalInfo, location])

	useEffect(() => {
		tracking.addShippingInfo(formattedOrder)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (loungerTypes !== undefined && parkingTickets !== undefined && baseProducts !== undefined && shoppingCart !== undefined) {
			formatOrder(loungerTypes, parkingTickets, baseProducts)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loungerTypes, parkingTickets, baseProducts, shoppingCart, massages, invoiceAddress, deliveryAddress])

	if (formatError) {
		return <RetryError show loading={formatFetching} onRetry={() => formatOrder(loungerTypes, parkingTickets, baseProducts)} />
	}

	return (
		<OrderSubPage>
			{formatFetching && (
				<div className="absolute h-100 w-100 z-9 flex-center" style={{ backgroundColor: "rgba(0,0,0,0.6)" }}>
					<FontAwesomeIcon icon={faCircleNotch} className="fa-spin fa-4x color-fg-white" />
				</div>
			)}

			{!stringLoading && linkData && (
				<>
					<ContentHeader canGoBack={amazonCheckoutSessionId == undefined} colorStyle="white" animation={OrderAnimationTypes.fullSlide}>
						{orderOverviewTitle}
					</ContentHeader>
					<OrderContentBody className="color-fg-primary flex flex-column h-100">
						{formattedOrder && (amazonCheckoutSessionId ? amazonPayData !== undefined : true) && (
							<div>
								<Addresses amazonPayData={amazonPayData} />
								<Entrances formattedOrder={formattedOrder} />
								<ParkingTickets formattedOrder={formattedOrder} notEditable />
								<Products formattedOrder={formattedOrder} />
								<Thermencard formattedOrder={formattedOrder} />
								<UsedVouchers formattedOrder={formattedOrder} />
								<PriceSummary formattedOrder={formattedOrder} />
							</div>
						)}

						<div className="mt-5">
							<div className="regular mb-2 f4">{orderOverviewMandatoryTitle}</div>
							<Checkbox
								className="f5"
								label={
									<RenderHTML
										html={orderOverviewAgbCheckbox.replace(
											"${link}",
											getLinkUrl(linkData) + appStore.get("core.languageCode") + "/" + linkData.links.find(e => e.type === LinkTypes.agb).slug,
										)}
									/>
								}
								formRegister={register("agb", { required: { value: true, message: errorInputRequiredField } })}
								errors={errors}
							/>

							<Checkbox
								className="mt-2 f5"
								label={
									<RenderHTML
										html={orderOverviewRevocationCheckbox.replace(
											"${link}",
											getLinkUrl(linkData) + appStore.get("core.languageCode") + "/" + linkData.links.find(e => e.type === LinkTypes.withdrawal).slug,
										)}
									/>
								}
								formRegister={register("revocation", { required: { value: true, message: errorInputRequiredField } })}
								errors={errors}
							/>
						</div>
						<div className="regular mt-5 f4">{orderOverviewEmailInformationTitle}</div>

						{/* <div className="mt-5">
                    <div className="regular mb-2 f4">
                        {orderOverviewNewsletterTitle}
                    </div>
                    <Checkbox className={"f5"} label={(<RenderHTML html={orderOverviewNewsletterCheckbox} />)} />
                </div> */}

						<div className="mt-2 f5">
							<RenderHTML html={addInfo} />
						</div>

						<LinkList />
					</OrderContentBody>
					<ContentFooter
						roundPrice={false}
						price={formattedOrder && !sm && !md ? formattedOrder.payAmount : undefined}
						priceAbsolute
						buttonProps={{
							text: buttonBuy,
							onNext: next,
							isLoading: isFetching || paymentGroupsLoading || amazonCheckoutSessionId ? amazonPayData === undefined : false,
							style: "secondary",
							disabled: shouldDisableBecauseOfTransporterCountry,
							disabledReason: shouldDisableBecauseOfTransporterCountry ? OrderErrors.deliveryCountryNotValid : undefined,
						}}
						center={sm || md}
					/>
				</>
			)}
		</OrderSubPage>
	)
}
